import { Injectable, ComponentRef, TemplateRef, createComponent, EnvironmentInjector, inject } from '@angular/core';
import { NzNotificationService, NzNotificationRef, NzNotificationDataOptions } from 'ng-zorro-antd/notification';
import { LiberroNotificationComponent } from '../shared/liberro-notification/liberro-notification.component';
import { take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private notificationService = inject(NzNotificationService);
  private injector = inject(EnvironmentInjector);
  private translateService = inject(TranslateService);

  private componentRefs = new Map<string, ComponentRef<LiberroNotificationComponent>>();

  public success(title: string, content: string, options?: NzNotificationDataOptions): NzNotificationRef {
    const { titleTemplate, key } = this.getTitleTemplate('success', title);
    const notificationRef = this.notificationService.blank(titleTemplate, content, options);
    this.setupCleanup(key, notificationRef);
    return notificationRef;
  }

  public error(title: string, content: string, options?: NzNotificationDataOptions): NzNotificationRef {
    const { titleTemplate, key } = this.getTitleTemplate('error', title);
    const notificationRef = this.notificationService.blank(titleTemplate, content, options);
    this.setupCleanup(key, notificationRef);
    return notificationRef;
  }

  public warning(title: string, content: string, options?: NzNotificationDataOptions): NzNotificationRef {
    const { titleTemplate, key } = this.getTitleTemplate('warning', title);
    const notificationRef = this.notificationService.blank(titleTemplate, content, options);
    this.setupCleanup(key, notificationRef);
    return notificationRef;
  }

  public unknownApiError(): void {
    this.error(
      this.translateService.instant('toastNotifications.headers.apiCallError'),
      this.translateService.instant('toastNotifications.messages.apiCallError'),
    );
  }

  private getTitleTemplate(type: 'success' | 'error' | 'warning', title: string): { titleTemplate: TemplateRef<void>; key: string } {
    const key = `${type}-${Date.now()}-${Math.random()}`;

    const componentRef = createComponent(LiberroNotificationComponent, {
      environmentInjector: this.injector
    });

    componentRef.instance.iconType = type;
    componentRef.instance.title = title;
    componentRef.changeDetectorRef.detectChanges();

    this.componentRefs.set(key, componentRef);

    return {
      titleTemplate: componentRef.instance.titleWithIconTemplate,
      key
    };
  }

  private setupCleanup(key: string, notificationRef: NzNotificationRef): void {
    notificationRef.onClose.pipe(take(1)).subscribe(() => {
      const ref = this.componentRefs.get(key);
      if (ref) {
        ref.destroy();
        this.componentRefs.delete(key);
      }
    });
  }
}
